import { useState, useEffect, useRef } from 'react';
import { format, subDays, subMonths } from 'date-fns';
import { useNavigate } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import DashboardLayout from '@/components/layout/DashboardLayout';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Badge } from '@/components/ui/badge';
import { Switch } from '@/components/ui/switch';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@/components/ui/tooltip';
import { Search as SearchIcon, X, Loader2, FileSearch, FileText, Bell, Download, Mail, AlertTriangle, LogIn, TrendingUp, HelpCircle, Building2 } from 'lucide-react';
import SearchResultsAI from '@/components/SearchResultsAI';
import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert';
import { supabase } from '@/integrations/supabase/client';
import { useToast } from '@/hooks/use-toast';
import { useAuth } from '@/hooks/useAuth';
import { formatDateLocalized } from '@/hooks/useLocationDetection';
import CpvSearch from '@/components/CpvSearch';
import CpvBadge from '@/components/CpvBadge';
import { cn } from '@/lib/utils';
import { EU_COUNTRIES, getCountryName } from '@/lib/countries';
import { useTranslation } from 'react-i18next';

type DatePreset = 'week' | 'month' | '3months' | '6months' | 'custom' | '';

const DATE_PRESET_KEYS: DatePreset[] = ['week', 'month', '3months', '6months', 'custom'];

interface WinnerInfo {
  lotNumber?: string;
  name: string;
  vatNumber: string;
}

interface TedResult {
  id: string;
  ted_notice_id: string;
  title: string;
  description: string;
  buyer_name: string;
  country: string;
  cpv_codes: string[];
  estimated_value: number | null;
  currency: string;
  publication_date: string;
  deadline_date: string | null;
  ted_url: string;
  pdf_url: string;
  notice_type?: string;
  winner_name?: string;
  winner_vat_number?: string;
  winners?: WinnerInfo[];
}

// Notice types available in TED API
const NOTICE_TYPES = [
  { value: '', label: 'Tutti i tipi' },
  { value: 'cn-standard', label: 'Bando di gara (CN)' },
  { value: 'cn-social', label: 'Bando sociale' },
  { value: 'can-standard', label: 'Avviso di aggiudicazione (CAN)' },
  { value: 'can-social', label: 'Aggiudicazione sociale' },
  { value: 'pin-only', label: 'Avviso di preinformazione' },
  { value: 'pin-rtl', label: 'PIN riduzione termini' },
  { value: 'veat', label: 'Avviso di trasparenza' },
  { value: 'corr', label: 'Rettifica' },
];

const ALPHA2_TO_ALPHA3: Record<string, string> = {
  IT: 'ITA', DE: 'DEU', FR: 'FRA', ES: 'ESP', NL: 'NLD',
  BE: 'BEL', AT: 'AUT', PL: 'POL', PT: 'PRT', GR: 'GRC',
  SE: 'SWE', DK: 'DNK', FI: 'FIN', IE: 'IRL', CZ: 'CZE',
  RO: 'ROU', HU: 'HUN', BG: 'BGR', HR: 'HRV', SK: 'SVK',
  SI: 'SVN', LT: 'LTU', LV: 'LVA', EE: 'EST', LU: 'LUX',
  MT: 'MLT', CY: 'CYP',
};

function normalizeCountryCode(code: string): string {
  const upper = (code || '').toUpperCase().trim();
  if (!upper) return '';
  if (ALPHA2_TO_ALPHA3[upper]) return ALPHA2_TO_ALPHA3[upper];
  const validAlpha3 = new Set(Object.values(ALPHA2_TO_ALPHA3));
  if (validAlpha3.has(upper)) return upper;
  return '';
}

export default function AdminClientSearch() {
  const navigate = useNavigate();
  const { user, role } = useAuth();
  const { i18n, t } = useTranslation();
  const currentLang = i18n.language?.split('-')[0] || 'it';
  const { toast } = useToast();

  // Redirect non-admin users
  useEffect(() => {
    if (role && role !== 'admin') {
      navigate('/dashboard');
    }
  }, [role, navigate]);

  // Fetch clients list
  const { data: clients = [], isLoading: loadingClients } = useQuery({
    queryKey: ['admin-clients'],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('clients')
        .select('id, name, contact_email')
        .eq('is_active', true)
        .order('name');
      if (error) throw error;
      return data || [];
    },
  });

  // Selected client for search
  const [selectedClientId, setSelectedClientId] = useState<string>('');
  const selectedClient = clients.find(c => c.id === selectedClientId);

  // Search form state
  const [keywords, setKeywords] = useState('');
  const [cpvCodes, setCpvCodes] = useState<string[]>([]);
  const [country, setCountry] = useState<string | null>(null);
  const [noticeType, setNoticeType] = useState<string>('');
  const [minValue, setMinValue] = useState('');
  const [maxValue, setMaxValue] = useState('');
  const [dateFrom, setDateFrom] = useState<Date | undefined>();
  const [dateTo, setDateTo] = useState<Date | undefined>();
  const [datePreset, setDatePreset] = useState<DatePreset>('');
  const [includeExpired, setIncludeExpired] = useState(false);
  const [onlyWithDeadline, setOnlyWithDeadline] = useState(false);
  const [creatingMonitoring, setCreatingMonitoring] = useState(false);
  const [sendingTestEmail, setSendingTestEmail] = useState(false);

  // Pre-select country based on detected location
  useEffect(() => {
    if (country !== null) return;

    const tryApplyDetected = () => {
      const detected = localStorage.getItem('detectedCountry');
      if (detected) {
        setCountry(normalizeCountryCode(detected));
        return true;
      }
      return false;
    };

    if (tryApplyDetected()) return;

    let tries = 0;
    const interval = window.setInterval(() => {
      tries += 1;
      if (tryApplyDetected() || tries >= 20) {
        window.clearInterval(interval);
      }
    }, 250);

    return () => window.clearInterval(interval);
  }, [country]);

  const handleDatePreset = (preset: DatePreset) => {
    setDatePreset(preset);
    const today = new Date();
    
    switch (preset) {
      case 'week':
        setDateFrom(subDays(today, 7));
        setDateTo(today);
        break;
      case 'month':
        setDateFrom(subMonths(today, 1));
        setDateTo(today);
        break;
      case '3months':
        setDateFrom(subMonths(today, 3));
        setDateTo(today);
        break;
      case '6months':
        setDateFrom(subMonths(today, 6));
        setDateTo(today);
        break;
      case 'custom':
        break;
      default:
        setDateFrom(undefined);
        setDateTo(undefined);
    }
  };

  const clearDates = () => {
    setDateFrom(undefined);
    setDateTo(undefined);
    setDatePreset('');
  };

  const [results, setResults] = useState<TedResult[]>([]);
  const [searching, setSearching] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalResults, setTotalResults] = useState(0);
  const [lastSearchBody, setLastSearchBody] = useState<any>(null);
  const [authError, setAuthError] = useState<string | null>(null);
  const pageCacheRef = useRef<Map<number, TedResult[]>>(new Map());

  const RESULTS_PER_PAGE = 50;
  const totalPages = Math.ceil(totalResults / RESULTS_PER_PAGE);

  const handleSearch = async (page: number = 1, useCache: boolean = false) => {
    const keywordList = keywords
      .split(',')
      .map((k) => k.trim())
      .filter((k) => k.length > 0);

    const hasSearchCriteria = keywordList.length > 0 || cpvCodes.length > 0 || !!country || !!noticeType;

    if (!hasSearchCriteria) {
      toast({
        variant: "destructive",
        title: "Inserisci un criterio di ricerca",
        description: "Seleziona almeno: parole chiave, un codice CPV, un paese o un tipo di gara.",
      });
      return;
    }

    if (page === 1 && !useCache) {
      pageCacheRef.current.clear();
    }

    if (useCache && pageCacheRef.current.has(page)) {
      setResults(pageCacheRef.current.get(page)!);
      setCurrentPage(page);
      return;
    }

    setSearching(true);
    if (!useCache) setResults([]);

    try {
      const body = {
        ...(keywordList.length > 0 ? { keywords: keywordList } : {}),
        ...(cpvCodes.length > 0 ? { cpv_codes: cpvCodes } : {}),
        ...(country ? { country } : {}),
        ...(noticeType ? { notice_type: noticeType } : {}),
        ...(minValue ? { min_value: parseFloat(minValue) } : {}),
        ...(maxValue ? { max_value: parseFloat(maxValue) } : {}),
        ...(dateFrom ? { date_from: format(dateFrom, 'yyyyMMdd') } : {}),
        ...(dateTo ? { date_to: format(dateTo, 'yyyyMMdd') } : {}),
        include_expired: includeExpired,
        page: page,
        limit: RESULTS_PER_PAGE,
      };

      setLastSearchBody(body);

      const { data, error } = await supabase.functions.invoke('ted-search', {
        body,
      });

      if (error) throw error;
      if (data?.error) throw new Error(data.error);

      const rawResults = data?.results || [];
      let newResults = [...rawResults];
      
      if (!includeExpired) {
        const now = new Date();
        newResults = newResults.filter((r: TedResult) => {
          if (!r.deadline_date) return true;
          const deadline = new Date(r.deadline_date);
          return deadline >= now;
        });
      }
      
      if (onlyWithDeadline) {
        newResults = newResults.filter((r: TedResult) => r.deadline_date);
      }
      
      newResults.sort((a: TedResult, b: TedResult) => {
        if (!a.deadline_date && !b.deadline_date) return 0;
        if (!a.deadline_date) return 1;
        if (!b.deadline_date) return -1;
        return new Date(a.deadline_date).getTime() - new Date(b.deadline_date).getTime();
      });
      
      pageCacheRef.current.set(page, newResults);
      
      setResults(newResults);
      setTotalResults(data?.total || newResults.length);
      setCurrentPage(page);
      
      if (newResults.length === 0 && page === 1) {
        toast({
          title: "Nessun risultato",
          description: "La ricerca non ha prodotto risultati. Prova a modificare i criteri.",
        });
      } else if (page === 1) {
        toast({
          title: "Ricerca completata",
          description: `Trovati ${data.total || newResults.length} bandi`,
        });
      }
    } catch (error: any) {
      console.error('Search error:', error);
      
      const isAuthError = error.status === 401;
      
      if (isAuthError) {
        setAuthError('La tua sessione è scaduta. Effettua nuovamente il login per continuare.');
      } else {
        toast({
          variant: "destructive",
          title: "Errore di ricerca",
          description: error.message || "Si è verificato un errore durante la ricerca",
        });
      }
    } finally {
      setSearching(false);
    }
  };

  const handlePageChange = (newPage: number) => {
    if (newPage >= 1 && newPage <= totalPages && newPage !== currentPage) {
      handleSearch(newPage, true);
    }
  };

  // Create monitoring for selected client
  const handleCreateMonitoring = async () => {
    if (!selectedClientId) {
      toast({
        variant: "destructive",
        title: "Seleziona un cliente",
        description: "Seleziona un cliente per creare il monitoraggio",
      });
      return;
    }

    const keywordList = keywords
      .split(',')
      .map((k) => k.trim())
      .filter((k) => k.length > 0);

    if (keywordList.length === 0 && cpvCodes.length === 0 && !country) {
      toast({
        variant: "destructive",
        title: "Nessun criterio",
        description: "Configura almeno un criterio di ricerca",
      });
      return;
    }

    setCreatingMonitoring(true);
    try {
      const monitoringName = keywordList.length > 0 
        ? keywordList.slice(0, 2).join(', ') 
        : cpvCodes.length > 0 
          ? `CPV: ${cpvCodes[0]}` 
          : `Paese: ${country}`;

      const { error } = await supabase.from('ted_searches').insert({
        client_id: selectedClientId,
        name: monitoringName,
        keywords: keywordList,
        cpv_codes: cpvCodes,
        countries: country ? [country] : [],
        min_value: minValue ? parseFloat(minValue) : null,
        max_value: maxValue ? parseFloat(maxValue) : null,
        is_active: true,
      });

      if (error) throw error;

      toast({
        title: "Monitoraggio creato",
        description: `Monitoraggio creato per il cliente "${selectedClient?.name}"`,
      });

      navigate('/monitoring');
    } catch (error: any) {
      console.error('Error creating monitoring:', error);
      toast({
        variant: "destructive",
        title: "Errore",
        description: error.message || "Impossibile creare il monitoraggio",
      });
    } finally {
      setCreatingMonitoring(false);
    }
  };

  // Send test email
  const handleSendTestEmail = async () => {
    if (!user?.email) {
      toast({
        variant: "destructive",
        title: "Email non disponibile",
        description: "Effettua l'accesso per inviare l'email di test",
      });
      return;
    }

    if (results.length === 0) {
      toast({
        variant: "destructive",
        title: "Nessun risultato",
        description: "Effettua prima una ricerca per avere risultati da inviare",
      });
      return;
    }

    setSendingTestEmail(true);
    try {
      const escapeHtml = (str: string) => {
        return str
          .replace(/&/g, '&amp;')
          .replace(/</g, '&lt;')
          .replace(/>/g, '&gt;')
          .replace(/"/g, '&quot;')
          .replace(/'/g, '&#039;');
      };

      const sampleResults = results.slice(0, 5);
      const resultsHtml = sampleResults.map(r => `
        <div style="border: 1px solid #e5e7eb; border-radius: 8px; padding: 16px; margin-bottom: 16px;">
          <h3 style="margin: 0 0 8px 0; color: #1f2937;">${escapeHtml(r.title)}</h3>
          <p style="color: #6b7280; font-size: 14px; margin: 0 0 8px 0;">${escapeHtml(r.buyer_name || 'N/D')}</p>
          ${r.estimated_value ? `<p style="color: #059669; font-weight: bold;">€${r.estimated_value.toLocaleString()}</p>` : ''}
          ${r.deadline_date ? `<p style="color: #dc2626; font-size: 14px;">Scadenza: ${formatDateLocalized(r.deadline_date)}</p>` : ''}
          ${r.pdf_url ? `<a href="${r.pdf_url}" style="color: #2563eb;">Visualizza PDF</a>` : ''}
        </div>
      `).join('');

      const html = `
        <div style="font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif; max-width: 600px; margin: 0 auto;">
          <h1 style="color: #1f2937; border-bottom: 2px solid #3b82f6; padding-bottom: 16px;">
            🔔 Alert TED Monitor - Test (Admin)
          </h1>
          <p style="color: #4b5563;">Questa è un'email di test con ${sampleResults.length} risultati di esempio:</p>
          ${resultsHtml}
          <div style="margin-top: 24px; padding-top: 16px; border-top: 1px solid #e5e7eb; color: #9ca3af; font-size: 12px;">
            <p>Questa è un'email di test inviata da TED Monitor (Ricerca Clienti Admin).</p>
          </div>
        </div>
      `;

      const { data, error } = await supabase.functions.invoke('send-alert-email', {
        body: {
          to: user.email,
          subject: '🔔 TED Monitor - Email di Test (Admin)',
          html,
        },
      });

      if (error) throw error;

      toast({
        title: "Email inviata",
        description: `Email di test inviata a ${user.email}`,
      });
    } catch (error: any) {
      console.error('Error sending test email:', error);
      toast({
        variant: "destructive",
        title: "Errore invio email",
        description: error.message || "Impossibile inviare l'email di test",
      });
    } finally {
      setSendingTestEmail(false);
    }
  };

  // Export data with multi-winner support
  const getExportData = () => {
    // Determine the maximum number of winners across all results
    const maxWinners = results.reduce((max, r) => {
      const winnerCount = r.winners?.length || (r.winner_name ? 1 : 0);
      return Math.max(max, winnerCount);
    }, 0);

    return results.map(r => {
      const baseData: Record<string, string | number> = {
        'ID Bando': r.ted_notice_id,
        'Titolo': r.title,
        'Descrizione': r.description,
        'Ente Appaltante': r.buyer_name,
        'Paese': r.country,
        'Valore Stimato (€)': r.estimated_value || '',
        'Valuta': r.currency,
        'Data Pubblicazione': r.publication_date,
        'Scadenza': r.deadline_date || '',
        'Codici CPV': r.cpv_codes?.join(', ') || '',
      };

      // Add winner columns dynamically
      if (maxWinners > 0) {
        const winners = r.winners && r.winners.length > 0 
          ? r.winners 
          : (r.winner_name ? [{ name: r.winner_name, vatNumber: r.winner_vat_number || '', lotNumber: '' }] : []);

        for (let i = 0; i < maxWinners; i++) {
          const winner = winners[i];
          const suffix = maxWinners > 1 ? ` ${i + 1}` : '';
          baseData[`Lotto${suffix}`] = winner?.lotNumber || '';
          baseData[`Vincitore${suffix}`] = winner?.name || '';
          baseData[`P.IVA Vincitore${suffix}`] = winner?.vatNumber || '';
        }
      }

      baseData['URL PDF'] = r.pdf_url || '';
      return baseData;
    });
  };

  const handleExportExcel = async () => {
    if (results.length === 0) {
      toast({
        variant: "destructive",
        title: "Nessun risultato",
        description: "Effettua prima una ricerca per avere risultati da esportare",
      });
      return;
    }

    try {
      const { exportRowsToExcel } = await import('@/lib/excel');
      const exportData = getExportData();
      const fileName = `ted_search_admin_${format(new Date(), 'yyyy-MM-dd_HH-mm')}.xlsx`;
      await exportRowsToExcel(exportData, 'Risultati TED', fileName);

      toast({
        title: "Export completato",
        description: `Esportati ${results.length} risultati in Excel`,
      });
    } catch (error: any) {
      console.error('Export error:', error);
      toast({
        variant: "destructive",
        title: "Errore export",
        description: "Impossibile esportare i risultati",
      });
    }
  };

  const handleExportCSV = async () => {
    if (results.length === 0) {
      toast({
        variant: "destructive",
        title: "Nessun risultato",
        description: "Effettua prima una ricerca per avere risultati da esportare",
      });
      return;
    }

    try {
      const { rowsToCsv } = await import('@/lib/excel');
      const exportData = getExportData();
      const csv = rowsToCsv(exportData, ';');
      
      const blob = new Blob(['\ufeff' + csv], { type: 'text/csv;charset=utf-8;' });
      const url = URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `ted_search_admin_${format(new Date(), 'yyyy-MM-dd_HH-mm')}.csv`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(url);

      toast({
        title: "Export completato",
        description: `Esportati ${results.length} risultati in CSV`,
      });
    } catch (error: any) {
      console.error('Export error:', error);
      toast({
        variant: "destructive",
        title: "Errore export",
        description: "Impossibile esportare i risultati",
      });
    }
  };

  return (
    <DashboardLayout>
      <TooltipProvider>
      <div className="space-y-6 animate-fade-in">
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
          <div>
            <h1 className="text-3xl font-bold text-foreground">Ricerca Clienti</h1>
            <p className="text-muted-foreground mt-1">
              Esegui ricerche TED per conto dei clienti
            </p>
          </div>
          
          {/* Admin Unlimited Badge */}
          <div className="flex items-center gap-2 px-4 py-2 rounded-lg border bg-primary/10 border-primary/50 text-primary">
            <TrendingUp className="w-4 h-4" />
            <span className="text-sm font-medium">Ricerche illimitate (Admin)</span>
          </div>
        </div>

        {/* Client Selector */}
        <Card className="border-primary/20 bg-primary/5">
          <CardContent className="py-4">
            <div className="flex items-center gap-4">
              <Building2 className="w-5 h-5 text-primary" />
              <div className="flex-1">
                <Label className="text-sm font-medium">Seleziona Cliente</Label>
                <Select 
                  value={selectedClientId} 
                  onValueChange={setSelectedClientId}
                  disabled={loadingClients}
                >
                  <SelectTrigger className="mt-1">
                    <SelectValue placeholder="Seleziona un cliente per la ricerca..." />
                  </SelectTrigger>
                  <SelectContent>
                    {clients.map((client) => (
                      <SelectItem key={client.id} value={client.id}>
                        {client.name} {client.contact_email && `(${client.contact_email})`}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              {selectedClient && (
                <Badge variant="secondary" className="h-8">
                  {selectedClient.name}
                </Badge>
              )}
            </div>
          </CardContent>
        </Card>

        {/* Auth Error Banner */}
        {authError && (
          <Alert variant="destructive" className="border-destructive/50 bg-destructive/10">
            <AlertTriangle className="h-4 w-4" />
            <AlertTitle>Sessione scaduta</AlertTitle>
            <AlertDescription className="flex items-center justify-between">
              <span>{authError}</span>
              <Button 
                variant="outline" 
                size="sm" 
                className="ml-4 gap-2"
                onClick={() => {
                  supabase.auth.signOut();
                  navigate('/auth');
                }}
              >
                <LogIn className="w-4 h-4" />
                Accedi
              </Button>
            </AlertDescription>
          </Alert>
        )}

        {/* Search Form */}
        <Card className="border-0 shadow-sm">
          <CardContent className="py-4 space-y-3">
            {/* Row 1: Main filters */}
            <div className="flex flex-wrap items-start gap-3">
              {/* Keywords */}
              <div className="flex-1 min-w-[200px] space-y-1">
                <Tooltip>
                  <TooltipTrigger asChild>
                    <Label htmlFor="keywords" className="text-xs cursor-help inline-flex items-center gap-1">
                      {t('search.keywords')}
                      <HelpCircle className="w-3 h-3 text-muted-foreground" />
                    </Label>
                  </TooltipTrigger>
                  <TooltipContent>
                    <p>Inserisci una o più parole separate da virgola o alternativamente un CPV</p>
                  </TooltipContent>
                </Tooltip>
                <Input
                  id="keywords"
                  placeholder={t('search.keywordsPlaceholder')}
                  value={keywords}
                  onChange={(e) => setKeywords(e.target.value)}
                />
              </div>

              {/* CPV */}
              <div className="w-[250px] space-y-1">
                <Tooltip>
                  <TooltipTrigger asChild>
                    <Label className="text-xs cursor-help inline-flex items-center gap-1">
                      {t('search.cpvCodes')}
                      <HelpCircle className="w-3 h-3 text-muted-foreground" />
                    </Label>
                  </TooltipTrigger>
                  <TooltipContent>
                    <p>Inserire una parola o una parte della parola per trovare i codici corrispondenti</p>
                  </TooltipContent>
                </Tooltip>
                <CpvSearch 
                  selectedCodes={cpvCodes}
                  onCodesChange={setCpvCodes}
                />
              </div>

              {/* Country */}
              <div className="w-[150px] space-y-1">
                <Label className="text-xs">{t('search.country')}</Label>
                <Select value={country ?? "all"} onValueChange={(val) => setCountry(val === "all" ? "" : val)}>
                  <SelectTrigger className="h-9">
                    <SelectValue placeholder={t('common.all')} />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">{t('search.allCountries')}</SelectItem>
                    {EU_COUNTRIES.map((c) => (
                      <SelectItem key={c.code} value={c.code}>
                        {getCountryName(c.code, currentLang)}
                      </SelectItem>
                    ))}
                  </SelectContent>
              </Select>
              </div>

              {/* Notice Type */}
              <div className="w-[180px] space-y-1">
                <Tooltip>
                  <TooltipTrigger asChild>
                    <Label className="text-xs cursor-help inline-flex items-center gap-1">
                      Tipo di gara
                      <HelpCircle className="w-3 h-3 text-muted-foreground" />
                    </Label>
                  </TooltipTrigger>
                  <TooltipContent side="top" className="max-w-sm">
                    <div className="space-y-1.5 text-xs">
                      <p><strong>Bando di gara (CN):</strong> Gara d'appalto aperta per presentare offerte</p>
                      <p><strong>Avviso di aggiudicazione (CAN):</strong> Esito della gara con vincitore assegnato</p>
                      <p><strong>Avviso di preinformazione:</strong> Avviso anticipato di futura gara</p>
                      <p><strong>Avviso di trasparenza:</strong> Pubblicazione trasparenza ex ante</p>
                      <p><strong>PIN riduzione termini:</strong> Preinformazione per ridurre i termini</p>
                      <p><strong>Rettifica:</strong> Correzione a bando pubblicato</p>
                    </div>
                  </TooltipContent>
                </Tooltip>
                <Select 
                  value={noticeType || 'all'} 
                  onValueChange={(val) => setNoticeType(val === 'all' ? '' : val)}
                >
                  <SelectTrigger className="h-9">
                    <SelectValue placeholder="Tutti i tipi" />
                  </SelectTrigger>
                  <SelectContent>
                    {NOTICE_TYPES.map((type) => (
                      <SelectItem key={type.value || 'all'} value={type.value || 'all'}>
                        {type.label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              {/* Value Range */}
              <div className="w-[100px] space-y-1">
                <Label className="text-xs text-muted-foreground">
                  {t('search.minValue', 'Min €')}
                </Label>
                <Input
                  type="number"
                  placeholder="0"
                  value={minValue}
                  onChange={(e) => setMinValue(e.target.value)}
                  className="h-9"
                />
              </div>

              <div className="w-[100px] space-y-1">
                <Label className="text-xs text-muted-foreground">
                  {t('search.maxValue', 'Max €')}
                </Label>
                <Input
                  type="number"
                  placeholder="∞"
                  value={maxValue}
                  onChange={(e) => setMaxValue(e.target.value)}
                  className="h-9"
                />
              </div>

              {/* Search Button */}
              <Tooltip>
                <TooltipTrigger asChild>
                  <Button
                    onClick={() => handleSearch()}
                    disabled={searching}
                    className="h-9"
                  >
                    {searching ? (
                      <Loader2 className="w-4 h-4 animate-spin" />
                    ) : (
                      <>
                        <SearchIcon className="w-4 h-4 mr-1" />
                        {t('search.searchButton', 'Cerca')}
                      </>
                    )}
                  </Button>
                </TooltipTrigger>
                <TooltipContent>
                  <p>{t('tooltips.search')}</p>
                </TooltipContent>
              </Tooltip>
            </div>

            {/* Row 2: Date Period and Options */}
            <div className="flex flex-wrap items-center gap-4 pt-2 border-t">
              <div className="flex items-center gap-2 flex-wrap">
                <Label className="text-xs text-muted-foreground">{t('search.datePresets.period', 'Periodo')}:</Label>
                {DATE_PRESET_KEYS.filter(key => key !== 'custom').map((key) => (
                  <Button
                    key={key}
                    variant={datePreset === key ? "default" : "outline"}
                    size="sm"
                    onClick={() => handleDatePreset(key)}
                    className="text-xs h-7 px-3"
                  >
                    {t(`search.datePresets.${key}`, key)}
                  </Button>
                ))}
                
                {/* Custom date inputs */}
                <div className="flex items-center gap-1">
                  <Input
                    type="date"
                    value={dateFrom ? format(dateFrom, 'yyyy-MM-dd') : ''}
                    onChange={(e) => {
                      setDateFrom(e.target.value ? new Date(e.target.value) : undefined);
                      setDatePreset('custom');
                    }}
                    className="h-7 text-xs w-[130px]"
                    placeholder={t('search.from', 'Da')}
                  />
                  <span className="text-xs text-muted-foreground">-</span>
                  <Input
                    type="date"
                    value={dateTo ? format(dateTo, 'yyyy-MM-dd') : ''}
                    onChange={(e) => {
                      setDateTo(e.target.value ? new Date(e.target.value) : undefined);
                      setDatePreset('custom');
                    }}
                    className="h-7 text-xs w-[130px]"
                    placeholder={t('search.to', 'A')}
                  />
                </div>

                {(dateFrom || dateTo) && (
                  <Button
                    variant="ghost"
                    size="sm"
                    className="text-xs h-7 px-2"
                    onClick={clearDates}
                  >
                    <X className="w-3 h-3" />
                  </Button>
                )}
              </div>

              <div className="flex items-center gap-4 ml-auto">
                <div className="flex items-center gap-2">
                  <Switch
                    id="only-with-deadline"
                    checked={onlyWithDeadline}
                    onCheckedChange={setOnlyWithDeadline}
                  />
                  <Label htmlFor="only-with-deadline" className="text-xs cursor-pointer">
                    {t('search.onlyWithDeadline')}
                  </Label>
                </div>
                <div className="flex items-center gap-2">
                  <Switch
                    id="include-expired"
                    checked={includeExpired}
                    onCheckedChange={setIncludeExpired}
                  />
                  <Label htmlFor="include-expired" className="text-xs cursor-pointer">
                    {t('search.includeExpired')}
                  </Label>
                </div>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Results */}
        <div className="space-y-4">
          <div className="flex items-center justify-between flex-wrap gap-2">
            <h2 className="text-lg font-semibold">
              {t('search.results')} {results.length > 0 && `(${results.length})`}
            </h2>
            
            {/* Action buttons */}
            <div className="flex items-center gap-2">
              <Tooltip>
                <TooltipTrigger asChild>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={handleCreateMonitoring}
                    disabled={creatingMonitoring || !selectedClientId || (!keywords && cpvCodes.length === 0 && !country)}
                  >
                    {creatingMonitoring ? (
                      <Loader2 className="w-4 h-4 mr-1 animate-spin" />
                    ) : (
                      <Bell className="w-4 h-4 mr-1" />
                    )}
                    Crea Monitoraggio
                  </Button>
                </TooltipTrigger>
                <TooltipContent>
                  <p>Crea un monitoraggio per il cliente selezionato</p>
                </TooltipContent>
              </Tooltip>
              
              <Tooltip>
                <TooltipTrigger asChild>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={handleSendTestEmail}
                    disabled={sendingTestEmail || results.length === 0}
                  >
                    {sendingTestEmail ? (
                      <Loader2 className="w-4 h-4 mr-1 animate-spin" />
                    ) : (
                      <Mail className="w-4 h-4 mr-1" />
                    )}
                    Invia Test Email
                  </Button>
                </TooltipTrigger>
                <TooltipContent>
                  <p>{t('tooltips.sendTestEmail')}</p>
                </TooltipContent>
              </Tooltip>
              
              <Tooltip>
                <TooltipTrigger asChild>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={handleExportExcel}
                    disabled={results.length === 0}
                  >
                    <Download className="w-4 h-4 mr-1" />
                    Excel
                  </Button>
                </TooltipTrigger>
                <TooltipContent>
                  <p>{t('tooltips.exportExcel')}</p>
                </TooltipContent>
              </Tooltip>
              
              <Tooltip>
                <TooltipTrigger asChild>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={handleExportCSV}
                    disabled={results.length === 0}
                  >
                    <Download className="w-4 h-4 mr-1" />
                    CSV
                  </Button>
                </TooltipTrigger>
                <TooltipContent>
                  <p>{t('tooltips.exportCSV')}</p>
                </TooltipContent>
              </Tooltip>
            </div>
          </div>

          {/* AI Analysis Component */}
          <SearchResultsAI results={results} isSearching={searching} searchQuery={keywords} />

          {results.length === 0 ? (
              <Card className="border-0 shadow-sm">
                <CardContent className="py-16 text-center">
                  <FileSearch className="w-16 h-16 text-muted-foreground/30 mx-auto mb-4" />
                  <h3 className="text-lg font-medium text-muted-foreground mb-2">
                    {t('search.noResults', 'Nessun risultato')}
                  </h3>
                  <p className="text-sm text-muted-foreground mb-2">
                    {t('search.noResultsDescription', 'Configura i filtri e avvia una ricerca per trovare bandi di gara')}
                  </p>
                  <p className="text-xs text-muted-foreground/80 mb-4">
                    {t('search.noResultsHint', 'Suggerimento: prova parole chiave più generiche o rimuovi alcuni filtri')}
                  </p>
                </CardContent>
              </Card>
            ) : (
              <div className="space-y-4">
                {results.map((result) => (
                  <Card key={result.id} className="border-0 shadow-sm hover:shadow-md transition-shadow">
                    <CardContent className="p-6">
                      <div className="flex items-start justify-between gap-4">
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center gap-2 mb-2 flex-wrap">
                            <Badge variant="outline">{result.country}</Badge>
                            {result.notice_type && (
                              <Badge variant="secondary" className="text-xs">
                                {NOTICE_TYPES.find(t => t.value === result.notice_type)?.label || result.notice_type}
                              </Badge>
                            )}
                            <span className="text-sm text-muted-foreground">
                              {result.ted_notice_id}
                            </span>
                          </div>
                          <h3 className="font-semibold text-foreground mb-2 line-clamp-2">
                            {result.title}
                          </h3>
                          <p className="text-sm text-muted-foreground mb-3 line-clamp-2">
                            {result.description}
                          </p>
                          <div className="flex flex-wrap items-center gap-4 text-sm">
                            <span className="text-muted-foreground">
                              <strong>Ente:</strong> {result.buyer_name}
                            </span>
                            {result.estimated_value && (
                              <span className="font-medium text-primary">
                                €{result.estimated_value.toLocaleString()}
                              </span>
                            )}
                            {result.deadline_date && (
                              <span className="text-destructive">
                                Scadenza: {formatDateLocalized(result.deadline_date)}
                              </span>
                            )}
                          </div>
                          
                          {/* Winner Information - display all winners per lot */}
                          {(result.winners && result.winners.length > 0) ? (
                            <div className="mt-3 p-3 bg-accent/50 rounded-lg border border-accent space-y-1">
                              <div className="text-xs font-medium text-accent-foreground mb-1">
                                {result.winners.length === 1 ? 'Vincitore:' : `Vincitori (${result.winners.length}):`}
                              </div>
                              {result.winners.map((winner, idx) => (
                                <div key={idx} className="flex flex-wrap items-center gap-3 text-sm">
                                  {winner.lotNumber && (
                                    <Badge variant="outline" className="text-xs">
                                      Lotto {winner.lotNumber}
                                    </Badge>
                                  )}
                                  <span className="text-accent-foreground font-medium">
                                    {winner.name}
                                  </span>
                                  {winner.vatNumber && (
                                    <span className="text-muted-foreground text-xs">
                                      P.IVA: {winner.vatNumber}
                                    </span>
                                  )}
                                </div>
                              ))}
                            </div>
                          ) : (result.winner_name || result.winner_vat_number) && (
                            <div className="mt-3 p-3 bg-accent/50 rounded-lg border border-accent">
                              <div className="flex flex-wrap items-center gap-4 text-sm">
                                {result.winner_name && (
                                  <span className="text-accent-foreground">
                                    <strong>Vincitore:</strong> {result.winner_name}
                                  </span>
                                )}
                                {result.winner_vat_number && (
                                  <span className="text-muted-foreground">
                                    <strong>P.IVA:</strong> {result.winner_vat_number}
                                  </span>
                                )}
                              </div>
                            </div>
                          )}
                          
                          {result.cpv_codes?.length > 0 && (
                            <div className="flex flex-wrap gap-1 mt-3">
                              {[...new Set(result.cpv_codes)].slice(0, 3).map((cpv, index) => (
                                <CpvBadge key={`${result.id}-${cpv}-${index}`} code={cpv} showPrefix />
                              ))}
                              {[...new Set(result.cpv_codes)].length > 3 && (
                                <Badge variant="secondary" className="text-xs">
                                  +{[...new Set(result.cpv_codes)].length - 3}
                                </Badge>
                              )}
                            </div>
                          )}
                        </div>
                        {result.pdf_url && (
                          <div className="shrink-0">
                            <Button
                              variant="secondary"
                              size="sm"
                              asChild
                            >
                              <a
                                href={result.pdf_url}
                                target="_blank"
                                rel="noopener noreferrer"
                              >
                                <FileText className="w-4 h-4 mr-1" />
                                PDF
                              </a>
                            </Button>
                          </div>
                        )}
                      </div>
                    </CardContent>
                  </Card>
                ))}
                
                {/* Pagination */}
                {totalPages > 1 && (
                  <div className="flex items-center justify-center gap-2 pt-4">
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => handlePageChange(1)}
                      disabled={currentPage === 1 || searching}
                    >
                      ««
                    </Button>
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => handlePageChange(currentPage - 1)}
                      disabled={currentPage === 1 || searching}
                    >
                      «
                    </Button>
                    
                    <div className="flex items-center gap-1">
                      {/* Show page numbers */}
                      {Array.from({ length: Math.min(5, totalPages) }, (_, i) => {
                        let pageNum: number;
                        if (totalPages <= 5) {
                          pageNum = i + 1;
                        } else if (currentPage <= 3) {
                          pageNum = i + 1;
                        } else if (currentPage >= totalPages - 2) {
                          pageNum = totalPages - 4 + i;
                        } else {
                          pageNum = currentPage - 2 + i;
                        }
                        return (
                          <Button
                            key={pageNum}
                            variant={pageNum === currentPage ? "default" : "outline"}
                            size="sm"
                            onClick={() => handlePageChange(pageNum)}
                            disabled={searching}
                            className="w-9"
                          >
                            {pageNum}
                          </Button>
                        );
                      })}
                    </div>
                    
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => handlePageChange(currentPage + 1)}
                      disabled={currentPage === totalPages || searching}
                    >
                      »
                    </Button>
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => handlePageChange(totalPages)}
                      disabled={currentPage === totalPages || searching}
                    >
                      »»
                    </Button>
                    
                    <span className="text-sm text-muted-foreground ml-2">
                      Pagina {currentPage} di {totalPages} ({totalResults} risultati)
                    </span>
                  </div>
                )}
              </div>
            )}
        </div>
      </div>
      </TooltipProvider>
    </DashboardLayout>
  );
}
